import { isSupportedLength } from './lengths';
import type { EvaluatedGuess, KeyState, TileState } from './types';

/**
 * Standard Wordle evaluation with correct duplicate handling.
 * Pass 1 marks exact matches and consumes them from the answer's letter
 * pool; pass 2 marks remaining guess letters present only while the pool
 * still holds that letter.
 *
 * The board's width is the answer's length (spec §10.1) — the guess must
 * match it, and it must be a playable length.
 */
export function evaluateGuess(guess: string, answer: string): TileState[] {
  const g = guess.toLowerCase();
  const a = answer.toLowerCase();
  const length = a.length;
  if (g.length !== length || !isSupportedLength(length)) {
    throw new Error(
      `evaluateGuess expects two words of the same length, 4-7 letters; got ${g.length} and ${a.length}`,
    );
  }

  const tiles: TileState[] = new Array(length).fill('absent');
  const pool = new Map<string, number>();

  for (let i = 0; i < length; i++) {
    if (g[i] === a[i]) {
      tiles[i] = 'correct';
    } else {
      pool.set(a[i]!, (pool.get(a[i]!) ?? 0) + 1);
    }
  }
  for (let i = 0; i < length; i++) {
    if (tiles[i] === 'correct') continue;
    const remaining = pool.get(g[i]!) ?? 0;
    if (remaining > 0) {
      tiles[i] = 'present';
      pool.set(g[i]!, remaining - 1);
    }
  }
  return tiles;
}

const RANK: Record<KeyState, number> = { unused: 0, absent: 1, present: 2, correct: 3 };

/** Best state seen per guessed letter, for keyboard coloring. */
export function keyboardState(guesses: readonly EvaluatedGuess[]): Record<string, KeyState> {
  const out: Record<string, KeyState> = {};
  for (const { word, tiles } of guesses) {
    const w = word.toLowerCase();
    for (let i = 0; i < w.length; i++) {
      const letter = w[i]!;
      const next = tiles[i]!;
      if (RANK[next] > RANK[out[letter] ?? 'unused']) out[letter] = next;
    }
  }
  return out;
}
